import React from 'react';
import { BiSolidError } from 'react-icons/bi';

import styles from './ValidationMessages.module.scss';

import {
  DATA_CALCULATION_SETUP_VALUES,
  DATA_CALCULATION_TYPE,
  DATA_DIVISOR_DETERMINATION_SETUP_VALUES,
  MESSAGES_ALERT,
} from '../../../utils/constants';

const ValidationMessagesSingleSubmit = ({ data, enableDivisorOrStartingValue }) => {
  // Early‑exit if required data is missing.
  // Keeps the JSX clean and avoids defensive checks everywhere
  if (!data || !enableDivisorOrStartingValue) return null;

  // Destructure once so the conditions below are shorter and more readable
  const { calculation_setup, launch_point, divisor_determination_setup, starting_value } = data;

  const { statusDivisor, statusStarting } = enableDivisorOrStartingValue;

  // Normalized boolean helpers (use “is/has” naming for clarity)
  const isEodSetup = calculation_setup === DATA_CALCULATION_SETUP_VALUES.EOD;
  const isIntradaySetup = calculation_setup === DATA_CALCULATION_SETUP_VALUES.EOD_INTRADAY;

  const isLaunchImmediate = launch_point === DATA_CALCULATION_TYPE.IMMEDIATE;
  const isLaunchEod = launch_point === DATA_CALCULATION_TYPE.EOD;

  const isDivisorEnabled = !!statusDivisor;
  const isStartingEnabled = !!statusStarting && !!starting_value;

  const isDeterminationImmediately =
    divisor_determination_setup === DATA_DIVISOR_DETERMINATION_SETUP_VALUES.IMMEDIATELY;
  const isDeterminationPreviousEod =
    divisor_determination_setup === DATA_DIVISOR_DETERMINATION_SETUP_VALUES.PREVIOUS_EOD;

  // Domain‑specific predicates with intention‑revealing names
  // These mirror your original booleans but read like rules instead of formulas
  const isDivisorEod = isDivisorEnabled && isEodSetup && isLaunchEod;

  const isDivisorIntradayImmediate = isDivisorEnabled && isIntradaySetup && isLaunchImmediate;

  const isDivisorIntradayEod = isDivisorEnabled && isIntradaySetup && isLaunchEod;

  const isStartingValueDeterminationPointImmediately =
    isStartingEnabled && isEodSetup && isDeterminationImmediately && isLaunchEod;

  const isStartingValueDeterminationPointPreviousEod =
    isStartingEnabled && isIntradaySetup && isDeterminationPreviousEod && isLaunchImmediate;

  const isStartingValueImmediatelyImmediate =
    isStartingEnabled && isIntradaySetup && isDeterminationImmediately && isLaunchImmediate;

  // Ordered list of rules mapping condition -> message
  // This replaces the if/else chain and makes the matching order explicit
  const rules = [
    {
      predicate: isDivisorEod || isDivisorIntradayImmediate || isDivisorIntradayEod,
      message: MESSAGES_ALERT.divisor,
    },
    {
      predicate: isStartingValueDeterminationPointImmediately,
      message: MESSAGES_ALERT.startingValueImmediatelyLaunchEOD,
    },
    {
      predicate: isStartingValueDeterminationPointPreviousEod,
      message: MESSAGES_ALERT.startingValuePreviousEODLaunchImmediate,
    },
    {
      predicate: isStartingValueImmediatelyImmediate,
      message: MESSAGES_ALERT.startingValueImmediatelyLaunchImmediate,
    },
  ];

  // Find first matching rule (keeps existing priority semantics)
  const firstMatch = rules.find(rule => rule.predicate);
  const message = firstMatch?.message;

  // If no rule matches, nothing to render
  if (!message) return null;

  // The JSX is now focused only on presentation, not business logic
  return (
    <div className={styles.messageDivisorStartingValue}>
      <BiSolidError className="icon-error" />
      {message}
    </div>
  );
};

export default ValidationMessagesSingleSubmit;
